import React from 'react';
import { AlertCircle, LucideIcon, LayoutDashboard, Building, Users, BarChart2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types/Auth';

interface NavItem {
    id: string;
    labelKey: string;
    icon: LucideIcon;
    allowedRoles: UserRole[];
}

interface SidebarProps {
    activeNavItem: string;
    onNavItemClick: (itemId: string) => void;
    isOpen: boolean;
    onClose: () => void;
}

const allNavItems: NavItem[] = [
    { id: 'dashboard', labelKey: 'sidebar.dashboard', icon: LayoutDashboard, allowedRoles: ['super-admin', 'admin', 'incidence-manager', 'it-member'] },
    { id: 'companies', labelKey: 'sidebar.companies', icon: Building, allowedRoles: ['super-admin'] },
    { id: 'users', labelKey: 'sidebar.users', icon: Users, allowedRoles: ['super-admin', 'admin'] },
    { id: 'incidents', labelKey: 'sidebar.incidents', icon: AlertCircle, allowedRoles: ['super-admin', 'admin', 'incidence-manager', 'it-member'] },
    { id: 'statistics', labelKey: 'sidebar.statistics', icon: BarChart2, allowedRoles: ['super-admin', 'incidence-manager', 'it-member'] },
    { id: 'my-company', labelKey: 'sidebar.myCompany', icon: Building, allowedRoles: ['admin'] },
];

export default function Sidebar({ activeNavItem, onNavItemClick, isOpen, onClose }: SidebarProps) {
    const { t } = useLanguage();
    const { state: authState } = useAuth();

    const userRoles = authState.user?.roles || [];

    const navItems = allNavItems.filter(item => 
        item.allowedRoles.some(role => userRoles.includes(role))
    );

    const sidebarClasses = isOpen
        ? 'fixed inset-y-0 left-0 z-30 w-64 bg-[#2E1C87] text-white transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0'
        : 'fixed inset-y-0 left-0 z-30 w-64 bg-[#2E1C87] text-white transform -translate-x-full transition-transform duration-300 ease-in-out md:relative md:translate-x-0';

    return (
        <>
            {isOpen && <div className="fixed inset-0 bg-black opacity-50 z-20 md:hidden" onClick={onClose}></div>}
            <div className={sidebarClasses}>
                <div className="p-6 flex justify-between items-center">
                    <h1 className="text-xl font-bold">Smart-IM</h1>
                    <button onClick={onClose} className="md:hidden p-1">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <nav className="mt-8">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.id}
                                onClick={() => { onNavItemClick(item.id); onClose(); }}
                                className={`w-full flex items-center px-6 py-3 text-left hover:bg-indigo-800 transition-colors ${
                                    activeNavItem === item.id ? 'bg-indigo-900 border-r-4 border-indigo-400' : ''
                                }`}
                            >
                                <Icon className="w-5 h-5 mr-3" />
                                {t(item.labelKey)}
                            </button>
                        );
                    })}
                </nav>
            </div>
        </>
    );
}
